import {
  ChatAlt2Icon,
  CheckIcon,
  DocumentTextIcon,
  DuplicateIcon,
  FilmIcon,
  KeyIcon,
  LinkIcon,
  MicrophoneIcon,
  PhotographIcon,
  SparklesIcon,
  VolumeUpIcon,
} from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import { ReactNode, useEffect, useState } from 'react';
import { SiAnthropic, SiMeta, SiOpenai, SiWindsurf } from 'react-icons/si';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import Seo from '@/components/Seo';

import { MCP_ENDPOINT } from '@/constants/mcpServer';
import {
  MUSE_CONNECT_PROMPT,
  MUSE_CONNECTOR_BRIEF_URL,
} from '@/constants/museConnector';

const Footer = dynamic(() => import('@/components/layout/Footer'));

const MEDIA = 'https://media.easy-peasy.ai/landing/home';
const CHATGPT_PLUGIN_URL =
  process.env.NEXT_PUBLIC_CHATGPT_PLUGIN_URL || 'https://chatgpt.com/plugins';
const CHATGPT_PLUGIN_ACTION_LABEL = process.env.NEXT_PUBLIC_CHATGPT_PLUGIN_URL
  ? 'Add to ChatGPT'
  : 'Open ChatGPT Plugins';

type ClientKey =
  | 'chatgpt'
  | 'claude'
  | 'muse'
  | 'claude-code'
  | 'cursor'
  | 'windsurf';

interface ClientTab {
  key: ClientKey;
  label: string;
  steps: string[];
  snippetLabel: string;
  snippet: string;
  actionHref?: string;
  actionLabel?: string;
  /** Shown under the snippet instead of the generic API-key / OAuth note. */
  note?: ReactNode;
}

const CLIENT_TABS: ClientTab[] = [
  {
    key: 'chatgpt',
    label: 'ChatGPT',
    steps: [
      'Open the ChatGPT Plugins Directory.',
      'Find Easy-Peasy.AI and select Add.',
      'Sign in to Easy-Peasy.AI and approve access — no API key needed.',
    ],
    snippetLabel: 'ChatGPT plugin',
    snippet: CHATGPT_PLUGIN_URL,
    actionHref: CHATGPT_PLUGIN_URL,
    actionLabel: CHATGPT_PLUGIN_ACTION_LABEL,
  },
  {
    key: 'claude',
    label: 'Claude',
    steps: [
      'Copy the Easy-Peasy.AI connector URL.',
      'In claude.ai or Claude Desktop, go to Settings → Connectors → Add custom connector, and paste the URL.',
      'Click Connect, sign in to Easy-Peasy.AI, and approve — no API key needed.',
    ],
    snippetLabel: 'Connector URL',
    snippet: MCP_ENDPOINT,
  },
  {
    key: 'muse',
    label: 'Meta Muse',
    steps: [
      'Copy the message below and send it to Muse — it builds the connector itself from our MCP server.',
      'When Muse asks for your API key, paste it into its secure credentials prompt (never into the chat).',
      'Muse tests the connection and saves it as a skill. From then on, just ask it to write, design, or narrate with Easy-Peasy.',
    ],
    snippetLabel: 'Message to Muse',
    snippet: MUSE_CONNECT_PROMPT,
    note: (
      <>
        Muse has no &ldquo;add MCP server&rdquo; setting — you connect by
        asking. Your key goes into Muse&apos;s Secure Credentials Store, so the
        agent itself never sees it. Muse reads the full connector brief at{' '}
        <a
          href={MUSE_CONNECTOR_BRIEF_URL}
          target='_blank'
          rel='noreferrer'
          className='font-mono text-xs font-bold text-indigo-300 hover:text-indigo-200'
        >
          {MUSE_CONNECTOR_BRIEF_URL.replace('https://', '')}
        </a>
        .
      </>
    ),
  },
  {
    key: 'claude-code',
    label: 'Claude Code',
    steps: [
      'Copy your API key from Easy-Peasy.AI Settings.',
      'Run this command in your terminal.',
      'Ask Claude to generate an image, write with a template, or create a video.',
    ],
    snippetLabel: 'Terminal',
    snippet: `claude mcp add easy-peasy --transport http ${MCP_ENDPOINT} --header "x-api-key: YOUR_API_KEY"`,
  },
  {
    key: 'cursor',
    label: 'Cursor',
    steps: [
      'Open Cursor Settings → MCP → Add new global MCP server.',
      'Paste this into your mcp.json with your API key.',
      'Ask the agent to use Easy-Peasy tools.',
    ],
    snippetLabel: 'mcp.json',
    snippet: `{
  "mcpServers": {
    "easy-peasy": {
      "url": "${MCP_ENDPOINT}",
      "headers": { "x-api-key": "YOUR_API_KEY" }
    }
  }
}`,
  },
  {
    key: 'windsurf',
    label: 'Windsurf',
    steps: [
      'Open Windsurf Settings → Cascade → MCP servers.',
      'Add this to your mcp_config.json with your API key.',
      'Cascade can now generate content on Easy-Peasy.AI.',
    ],
    snippetLabel: 'mcp_config.json',
    snippet: `{
  "mcpServers": {
    "easy-peasy": {
      "serverUrl": "${MCP_ENDPOINT}",
      "headers": { "x-api-key": "YOUR_API_KEY" }
    }
  }
}`,
  },
];

const CAPABILITIES = [
  {
    name: 'Write with 200+ templates',
    description:
      'Blog posts, product descriptions, social captions, emails — run any Easy-Peasy.AI template straight from your AI chat.',
    tools: ['list_templates', 'generate_text'],
    icon: DocumentTextIcon,
  },
  {
    name: 'Generate & edit images',
    description:
      'Create images with Nano Banana, FLUX, Seedream and more — or edit existing images by passing their URLs.',
    tools: ['generate_image'],
    icon: PhotographIcon,
  },
  {
    name: 'Create videos',
    description:
      'Text-to-video and image-to-video with Veo, Seedance, Kling and other top models, with soundtrack generation.',
    tools: ['generate_video', 'get_video'],
    icon: FilmIcon,
  },
  {
    name: 'Text to speech',
    description:
      'Turn text into natural speech with 1,000+ voices — including your own cloned voices.',
    tools: ['list_voices', 'generate_speech'],
    icon: VolumeUpIcon,
  },
  {
    name: 'Transcribe audio & video',
    description:
      'Accurate transcription with speaker detection for podcasts, meetings, and interviews.',
    tools: ['transcribe_audio'],
    icon: MicrophoneIcon,
  },
  {
    name: 'One account, one bill',
    description:
      'Everything runs on your Easy-Peasy.AI account — same credits, same quotas, same history as the web app.',
    tools: ['get_account'],
    icon: SparklesIcon,
  },
];

const STEPS = [
  {
    name: 'Have an account ready',
    description:
      'ChatGPT and Claude just need you to sign in and approve. CLI clients use the API key from your Settings page.',
    icon: KeyIcon,
  },
  {
    name: 'Connect your AI client',
    description:
      'Add the ChatGPT plugin, paste the connector URL into Claude, ask Meta Muse to build the connector, or connect a CLI client with your API key.',
    icon: LinkIcon,
  },
  {
    name: 'Just ask',
    description:
      '"Generate a hero image for my landing page" or "Write 5 product descriptions" — your AI does the rest.',
    icon: ChatAlt2Icon,
  },
];

const FAQS = [
  {
    question: 'What is MCP?',
    answer:
      'The Model Context Protocol is an open standard that lets AI assistants like ChatGPT and Claude use external tools. Connecting Easy-Peasy.AI gives your assistant the ability to generate text, images, videos, and speech on your account — without leaving the chat.',
  },
  {
    question: 'How is usage billed?',
    answer:
      'Exactly like the web app and the REST API: generations use the credits and quotas of the plan on the account whose API key you connect. Nothing extra to pay for the MCP server itself.',
  },
  {
    question: 'Which AI clients are supported?',
    answer:
      'ChatGPT, Claude (claude.ai and Claude Desktop connectors), Meta Muse, Claude Code, Cursor, Windsurf, VS Code, and any other MCP client. ChatGPT and Claude connect with OAuth sign-in; other clients can also use your API key as a header.',
  },
  {
    question: 'How do I connect Meta Muse?',
    answer:
      'Muse builds connectors on request: send it the message from the Meta Muse tab above, hand over your API key when its secure credentials prompt appears, and it saves Easy-Peasy.AI as a reusable skill. Muse works from the same MCP server as every other client, so nothing else to install.',
  },
  {
    question: 'Where do I find my API key?',
    answer:
      'On your Settings page at easy-peasy.ai/settings, in the API section. Treat it like a password — anyone with the key can generate on your account.',
  },
  {
    question: 'Is there also a REST API?',
    answer:
      'Yes — the MCP server exposes the same capabilities as our REST API, documented at easy-peasy.ai/docs. Use whichever fits your workflow; they share the same key.',
  },
];

// Marketing + setup page rendered at /mcp for browsers. MCP protocol
// traffic to the same URL is routed to /api/mcp by header-matching
// rewrites in next.config.mjs.
const McpLanding = () => {
  const [activeTab, setActiveTab] = useState<ClientKey>('chatgpt');
  const [copied, setCopied] = useState<string | null>(null);

  // Deep links like /mcp#muse preselect a client tab (used from docs and
  // the Muse directory listing).
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (CLIENT_TABS.some((t) => t.key === hash)) {
      setActiveTab(hash as ClientKey);
    }
  }, []);

  const copyText = (id: string, text: string) => {
    navigator.clipboard?.writeText(text).then(
      () => {
        setCopied(id);
        setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000);
      },
      () => setCopied(null),
    );
  };

  const tab = CLIENT_TABS.find((t) => t.key === activeTab) || CLIENT_TABS[0];

  return (
    <main className='relative bg-[#07070C]'>
      <Seo
        templateTitle='ChatGPT Plugin & MCP Server — Easy-Peasy.AI'
        description='Add Easy-Peasy.AI to ChatGPT or connect Claude and other MCP clients. Generate text, images, videos, and speech straight from your AI chat.'
        image='https://media.easy-peasy.ai/landing/mcp/og-mcp.jpg'
      />

      {/* Hero */}
      <section className='relative overflow-hidden bg-[#050509]'>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`${MEDIA}/final-cta-bg.webp`}
          alt=''
          className='absolute inset-0 h-full w-full object-cover opacity-70'
        />
        <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_60%_55%_at_50%_45%,rgba(5,5,9,0.75),rgba(5,5,9,0.5)_65%,rgba(5,5,9,0.3))]' />
        <div className='pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#07070C] to-transparent' />

        <div className='absolute top-0 left-0 w-full z-30'>
          <LandingPageNavigation variant='light' forceDark />
        </div>

        <div className='relative z-20 mx-auto max-w-4xl px-4 pt-36 pb-20 text-center sm:px-6 sm:pt-44'>
          <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-white/15 bg-black/30 px-4 py-1.5 text-xs font-semibold uppercase tracking-widest text-slate-200 backdrop-blur-md sm:text-sm'>
            <span className='rounded-full bg-indigo-500 px-2 py-0.5 text-[10px] font-bold text-white'>
              NEW
            </span>
            ChatGPT Plugin · Model Context Protocol
          </div>

          <h1 className='text-4xl font-extrabold tracking-tight text-white drop-shadow-2xl sm:text-5xl md:text-6xl lg:text-7xl'>
            <span className='block'>Every Easy-Peasy tool,</span>
            <span className='mt-1 block bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text pb-2 text-transparent'>
              inside your AI chat.
            </span>
          </h1>

          <p className='mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-slate-200 drop-shadow-lg md:text-xl'>
            Connect once and create from the chats you already use — 200+
            writing templates, images, videos, speech and transcription, all
            billed to your Easy-Peasy.AI account.
          </p>

          {/* The endpoint is the hero action: copy it, paste it, done. */}
          <div className='mx-auto mt-10 flex w-full max-w-2xl items-center gap-2 rounded-full border border-white/20 bg-black/40 py-2 pl-5 pr-2 backdrop-blur-xl'>
            <code className='min-w-0 flex-1 truncate text-left font-mono text-sm text-slate-200 sm:text-base'>
              {MCP_ENDPOINT}
            </code>
            <button
              type='button'
              onClick={() => copyText('endpoint', MCP_ENDPOINT)}
              className='inline-flex shrink-0 items-center gap-1.5 rounded-full bg-indigo-500 px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-indigo-400'
            >
              {copied === 'endpoint' ? (
                <CheckIcon className='h-4 w-4' />
              ) : (
                <DuplicateIcon className='h-4 w-4' />
              )}
              {copied === 'endpoint' ? 'Copied' : 'Copy URL'}
            </button>
          </div>
          <div className='mt-3 text-xs font-medium uppercase tracking-widest text-slate-400'>
            ChatGPT plugin · Claude connector · Meta Muse · Any MCP client
          </div>

          <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
            <a
              href={CHATGPT_PLUGIN_URL}
              target='_blank'
              rel='noreferrer'
              className='inline-flex h-[54px] min-w-[200px] items-center justify-center rounded-full bg-white px-8 text-lg font-bold text-slate-900 transition-all hover:bg-slate-100'
            >
              <SiOpenai className='mr-2 h-5 w-5' aria-hidden='true' />
              {CHATGPT_PLUGIN_ACTION_LABEL}
            </a>
            <a
              href='#setup'
              className='inline-flex h-[54px] min-w-[170px] items-center justify-center rounded-full border border-white/25 bg-black/25 px-8 text-lg font-semibold text-white backdrop-blur-md transition-all hover:border-white/45 hover:bg-black/40'
            >
              Other clients
            </a>
          </div>

          {/* Supported clients */}
          <div className='mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-white/50'>
            <span className='inline-flex items-center gap-2 text-base'>
              <SiOpenai className='h-4 w-4' aria-hidden='true' /> ChatGPT
            </span>
            <span className='inline-flex items-center gap-2 text-base'>
              <SiAnthropic className='h-4 w-4' aria-hidden='true' /> Claude
            </span>
            <span className='inline-flex items-center gap-2 text-base'>
              <SiMeta className='h-4 w-4' aria-hidden='true' /> Meta Muse
            </span>
            <span className='text-base'>Claude Code</span>
            <span className='text-base'>Cursor</span>
            <span className='inline-flex items-center gap-2 text-base'>
              <SiWindsurf className='h-4 w-4' aria-hidden='true' /> Windsurf
            </span>
            <span className='text-base'>VS Code</span>
            <span className='text-base'>Any MCP client</span>
          </div>
        </div>
      </section>

      {/* Setup */}
      <section id='setup' className='relative py-20 lg:py-28'>
        <div className='mx-auto max-w-4xl px-4 sm:px-6 lg:px-8'>
          <div className='mb-12 text-center'>
            <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
              Connected in under a minute
            </h2>
            <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
              Pick your client — ChatGPT and Claude need nothing but a sign-in,
              and Muse builds the connector for you.
            </p>
          </div>

          <div className='rounded-3xl border border-white/10 bg-white/[0.04] p-5 sm:p-8'>
            <div className='flex flex-wrap gap-2'>
              {CLIENT_TABS.map((clientTab) => (
                <button
                  key={clientTab.key}
                  type='button'
                  onClick={() => setActiveTab(clientTab.key)}
                  className={clsxm(
                    'rounded-full px-5 py-2 text-sm font-bold transition-all',
                    activeTab === clientTab.key
                      ? 'bg-indigo-500 text-white shadow-[0_8px_30px_-8px_rgba(99,102,241,0.7)]'
                      : 'border border-white/10 bg-white/[0.04] text-slate-300 hover:border-white/25 hover:text-white',
                  )}
                >
                  {clientTab.label}
                </button>
              ))}
            </div>

            <ol className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-3'>
              {tab.steps.map((step, index) => (
                <li
                  key={step}
                  className='rounded-2xl border border-white/10 bg-white/[0.04] p-5'
                >
                  <span className='flex h-7 w-7 items-center justify-center rounded-full bg-indigo-500/15 text-xs font-bold text-indigo-300 ring-1 ring-inset ring-indigo-400/20'>
                    {index + 1}
                  </span>
                  <p className='mt-3 text-sm leading-relaxed text-slate-400'>
                    {step}
                  </p>
                </li>
              ))}
            </ol>

            {tab.actionHref ? (
              <div className='mt-5 flex flex-col items-center justify-between gap-4 rounded-2xl border border-white/10 bg-black/60 px-5 py-6 sm:flex-row'>
                <div>
                  <div className='text-xs font-bold uppercase tracking-widest text-slate-500'>
                    {tab.snippetLabel}
                  </div>
                  <div className='mt-1 text-sm text-slate-300'>
                    Install from the official ChatGPT Plugins Directory.
                  </div>
                </div>
                <a
                  href={tab.actionHref}
                  target='_blank'
                  rel='noreferrer'
                  className='inline-flex shrink-0 items-center justify-center rounded-full bg-white px-6 py-3 text-sm font-bold text-slate-900 transition-colors hover:bg-slate-100'
                >
                  <SiOpenai className='mr-2 h-4 w-4' aria-hidden='true' />
                  {tab.actionLabel}
                </a>
              </div>
            ) : (
              <div className='mt-5 overflow-hidden rounded-2xl border border-white/10 bg-black/60'>
                <div className='flex items-center justify-between border-b border-white/10 px-4 py-2.5'>
                  <span className='text-xs font-bold uppercase tracking-widest text-slate-500'>
                    {tab.snippetLabel}
                  </span>
                  <button
                    type='button'
                    onClick={() => copyText(tab.key, tab.snippet)}
                    className='inline-flex items-center gap-1.5 text-xs font-bold text-slate-400 transition-colors hover:text-white'
                  >
                    {copied === tab.key ? (
                      <CheckIcon className='h-4 w-4' />
                    ) : (
                      <DuplicateIcon className='h-4 w-4' />
                    )}
                    {copied === tab.key ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <pre className='overflow-x-auto p-4 font-mono text-sm leading-6 text-slate-200'>
                  <code>{tab.snippet}</code>
                </pre>
              </div>
            )}

            {tab.note ? (
              <p className='mt-4 text-center text-sm text-slate-400'>
                {tab.note}
              </p>
            ) : tab.actionHref ? (
              <p className='mt-4 text-center text-sm text-slate-400'>
                ChatGPT opens Easy-Peasy.AI&apos;s secure OAuth sign-in. Your
                API key is never shared with ChatGPT.
              </p>
            ) : tab.snippet.includes('YOUR_API_KEY') ? (
              <p className='mt-4 text-center text-sm text-slate-400'>
                Replace <code className='font-mono text-xs'>YOUR_API_KEY</code>{' '}
                with the key from your{' '}
                <Link
                  href='/settings'
                  className='font-bold text-indigo-300 hover:text-indigo-200'
                >
                  Settings page
                </Link>
                .
              </p>
            ) : (
              <p className='mt-4 text-center text-sm text-slate-400'>
                You&apos;ll be asked to sign in to Easy-Peasy.AI and approve
                access — no API key needed.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* In action */}
      <section className='relative border-y border-white/[0.06] bg-white/[0.02] py-20 lg:py-28'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16'>
            <div>
              <div className='text-sm font-bold uppercase tracking-widest text-indigo-300'>
                In your chat
              </div>
              <h2 className='mt-3 text-3xl font-extrabold tracking-tight text-white sm:text-4xl'>
                Ask in plain language. Get real files back.
              </h2>
              <p className='mt-4 text-lg leading-relaxed text-slate-400'>
                Your assistant picks the right Easy-Peasy tool, runs it on your
                account, and hands you the result inline — no tab switching, no
                copy-paste, no separate bill.
              </p>

              {/* Illustrative exchange */}
              <div className='mt-8 space-y-3' aria-hidden='true'>
                <div className='ml-auto max-w-md rounded-2xl rounded-br-sm bg-indigo-500 px-5 py-3 text-white'>
                  Generate a cinematic hero image of a glass whale over a salt
                  flat at sunset
                </div>
                <div className='max-w-md rounded-2xl rounded-bl-sm border border-white/10 bg-white/[0.06] px-5 py-3 text-slate-300'>
                  <span className='mb-2 inline-flex items-center gap-2 rounded-md bg-black/40 px-2 py-1 font-mono text-xs text-indigo-300'>
                    <SparklesIcon className='h-3.5 w-3.5' /> generate_image
                  </span>
                  <p className='text-sm leading-relaxed'>
                    Done — created with Seedream 5.0 Pro and saved to your
                    Easy-Peasy library.
                  </p>
                </div>
              </div>
            </div>

            <div className='relative'>
              <div
                aria-hidden='true'
                className='pointer-events-none absolute -inset-6 rounded-[2.5rem] bg-gradient-to-br from-indigo-500/15 to-fuchsia-500/15 blur-2xl'
              />
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src='https://media.easy-peasy.ai/landing/image/hero-whale.webp'
                alt='Image generated through the Easy-Peasy MCP server'
                loading='lazy'
                decoding='async'
                className='relative w-full rounded-3xl border border-white/10 shadow-2xl'
              />
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className='relative py-20 lg:py-28'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='mb-12 text-center'>
            <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
              One connector, every tool
            </h2>
            <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
              The same capabilities as the web app, exposed as MCP tools.
            </p>
          </div>

          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 md:gap-4 lg:grid-cols-3'>
            {CAPABILITIES.map((capability) => (
              <div
                key={capability.name}
                className='flex flex-col rounded-2xl border border-white/10 bg-white/[0.04] p-6 transition-all hover:border-indigo-400/40 hover:bg-white/[0.07]'
              >
                <span className='flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300 ring-1 ring-inset ring-indigo-400/20'>
                  <capability.icon className='h-5 w-5' aria-hidden='true' />
                </span>
                <h3 className='mt-4 font-bold text-white'>{capability.name}</h3>
                <p className='mt-2 flex-1 text-sm leading-relaxed text-slate-400'>
                  {capability.description}
                </p>
                <div className='mt-4 flex flex-wrap gap-1.5'>
                  {capability.tools.map((tool) => (
                    <code
                      key={tool}
                      className='rounded-md bg-black/40 px-2 py-1 font-mono text-xs text-slate-400 ring-1 ring-inset ring-white/10'
                    >
                      {tool}
                    </code>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className='relative py-20 lg:py-28'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='mb-14 text-center'>
            <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
              Connected in three steps
            </h2>
          </div>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5'>
            {STEPS.map((step, index) => (
              <div
                key={step.name}
                className='rounded-2xl border border-white/10 bg-white/[0.04] p-8'
              >
                <div className='flex items-center gap-3'>
                  <span className='flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-indigo-500/15 text-indigo-300 ring-1 ring-inset ring-indigo-400/20'>
                    <step.icon className='h-5 w-5' aria-hidden='true' />
                  </span>
                  <span className='text-sm font-bold tracking-widest text-indigo-300'>
                    0{index + 1}
                  </span>
                </div>
                <h3 className='mt-4 text-xl font-bold text-white'>
                  {step.name}
                </h3>
                <p className='mt-2 leading-relaxed text-slate-400'>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className='relative border-y border-white/[0.06] bg-white/[0.02] py-20 lg:py-28'>
        <div className='mx-auto max-w-4xl px-4 sm:px-6 lg:px-8'>
          <h2 className='text-center text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            Frequently asked questions
          </h2>
          <dl className='mt-12 space-y-4'>
            {FAQS.map((faq) => (
              <div
                key={faq.question}
                className='rounded-2xl border border-white/10 bg-white/[0.04] p-6'
              >
                <dt className='font-bold text-white'>{faq.question}</dt>
                <dd className='mt-2 leading-relaxed text-slate-400'>
                  {faq.answer}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* CTA */}
      <section className='relative overflow-hidden bg-[#050509] py-28 lg:py-36'>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={`${MEDIA}/final-cta-bg.webp`}
          alt=''
          loading='lazy'
          decoding='async'
          className='absolute inset-0 h-full w-full object-cover'
        />
        <div className='pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_55%_50%_at_50%_50%,rgba(5,5,9,0.6),transparent_75%)]' />
        <div className='pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#07070C] to-transparent' />
        <div className='pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#07070C] to-transparent' />

        <div className='relative z-10 mx-auto max-w-4xl px-4 text-center sm:px-6'>
          <h2 className='text-5xl font-extrabold tracking-tight text-white sm:text-6xl'>
            Plug it in.{' '}
            <span className='bg-gradient-to-r from-indigo-300 via-violet-300 to-fuchsia-300 bg-clip-text text-transparent'>
              Easy-peasy.
            </span>
          </h2>
          <div className='mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row'>
            <button
              type='button'
              onClick={() => copyText('cta', MCP_ENDPOINT)}
              className='inline-flex h-[58px] min-w-[240px] items-center justify-center gap-2 rounded-full bg-indigo-500 px-8 text-lg font-bold text-white shadow-[0_8px_40px_-8px_rgba(99,102,241,0.7)] transition-all hover:bg-indigo-400'
            >
              {copied === 'cta' ? (
                <CheckIcon className='h-5 w-5' />
              ) : (
                <DuplicateIcon className='h-5 w-5' />
              )}
              {copied === 'cta' ? 'URL copied' : 'Copy connector URL'}
            </button>
            <Link
              href='/settings'
              className='inline-flex h-[58px] min-w-[180px] items-center justify-center rounded-full border border-white/20 bg-white/5 px-8 text-lg font-semibold text-white backdrop-blur-md transition-all hover:border-white/40 hover:bg-white/10'
            >
              Get your API key
            </Link>
          </div>
        </div>
      </section>

      <div className='dark'>
        <Footer variant='dark' />
      </div>
    </main>
  );
};

export default McpLanding;
